import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCollectionsV1Service, serviceName } from '../service';
import { PreviewCollectionRequestIO, ServiceQueries } from '../types';
import { useCollectionsV1Queries } from './use-collections-v1-queries';
import { UseCollectionsV1QueryEndpointArgs, useCollectionsV1Query } from './use-collections-v1-query';

/**
 * A hook that returns a query for the `PreviewCollectionRequest` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const usePreviewCollectionRequestQuery = <E = unknown, D = PreviewCollectionRequestIO['output']>(
  args: UseCollectionsV1QueryEndpointArgs<'PreviewCollectionRequest', E, D>
) =>
  useCollectionsV1Query({
    endpointName: 'PreviewCollectionRequest',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `PreviewCollectionRequest` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const usePreviewCollectionRequestQueries = <E = unknown, D = PreviewCollectionRequestIO['output']>(
  argsArr: UseCollectionsV1QueryEndpointArgs<'PreviewCollectionRequest', E, D>[]
) =>
  useCollectionsV1Queries(
    argsArr.map((args) => ({
      endpointName: 'PreviewCollectionRequest',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `PreviewCollectionRequest` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchPreviewCollectionRequest = () => {
  const queryClient = useQueryClient();

  const fetchPreviewCollectionRequest = async <E = unknown, D = PreviewCollectionRequestIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCollectionsV1QueryEndpointArgs<'PreviewCollectionRequest', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      PreviewCollectionRequestIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'PreviewCollectionRequest'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        PreviewCollectionRequestIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'PreviewCollectionRequest'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'PreviewCollectionRequest'>({
          serviceName,
          endpointName: 'PreviewCollectionRequest',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCollectionsV1Service.PreviewCollectionRequest(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchPreviewCollectionRequest;
};
